import { useState } from "react";
import api from "../services/api";

const CONTENT = {
    customer_agreement: {
        title: "Terms & Conditions and Privacy Policy",
        body: (
            <>
                <span className="agreement-updated">Last Updated: July 2026</span>

                <div className="agreement-summary">
                    <h4>In plain terms</h4>
                    <ul>
                        <li><strong>Your account is used</strong> to place orders, process payments, and deliver food to your saved addresses.</li>
                        <li><strong>We use your location</strong> to show nearby restaurants and help delivery partners find you — only for these purposes.</li>
                        <li><strong>Payment details</strong> are handled by our payment partner and are not stored on our servers.</li>
                        <li><strong>You can delete your account</strong> and data at any time from your profile settings.</li>
                    </ul>
                </div>

                <h3>1. Account & Orders</h3>
                <p>By creating a MealBee account, you agree to let us process your orders, payments, and delivery details in order to fulfil your requests on the Platform.</p>

                <h3>2. Location Data</h3>
                <p>MealBee collects your location to show nearby restaurants, calculate delivery distance and fees, and assist delivery partners in reaching your address.</p>

                <h3>3. Payments</h3>
                <p>Online payments are processed through our third-party payment gateway. MealBee does not store your card or bank details.</p>

                <h3>4. Your Rights</h3>
                <p>You may request account deletion or data export at any time through the app's Profile settings.</p>

                <p className="agreement-fullref">
                    Read the complete Terms & Conditions and Privacy Policy any time from your Profile.
                </p>
            </>
        )
    },
    restaurant_agreement: {
        title: "Restaurant Partner Agreement",
        body: (
            <>
                <span className="agreement-updated">Last Updated: July 2026</span>

                <div className="agreement-summary">
                    <h4>In plain terms</h4>
                    <ul>
                        <li><strong>Listing is non-exclusive</strong> — you can also sell elsewhere, unless you separately agree to an exclusivity arrangement in exchange for a reduced rate.</li>
                        <li><strong>You are responsible</strong> for your own FSSAI license, GST registration, and food safety compliance — MEAL BEE does not check food quality on your behalf.</li>
                        <li><strong>New Restaurant Partners pay 10% commission</strong> for their first 60 days or first 100 orders, whichever comes first. After that, the standard rate is 15% (up to 20% for optional premium placement). You will always be told your applicable rate in writing before it changes.</li>
                        <li><strong>MEAL BEE can use your restaurant name</strong>, logo, and menu photos to promote your listing on the Platform — nowhere else, without your separate written consent.</li>
                    </ul>
                </div>

                <h3>1. Appointment</h3>
                <p>Restaurant Partners hereby appoint MEAL BEE as a non-exclusive technology platform for listing menu items and facilitating the receipt and processing of Orders. This appointment does not create any partnership, joint venture, agency, or employment relationship.</p>

                <h3>2. Licenses & Compliance</h3>
                <p>Restaurant Partners shall, at all times, maintain all required registrations, licenses, permits, and approvals mandated under Applicable Laws, including a valid FSSAI license/registration, GST registration (if applicable), shop & establishment license, and any municipal or local authority permissions required for food business operations.</p>

                <h3>3. Commission Structure</h3>
                <p>Newcomer rate is 10% for the first 60 days or first 100 completed Orders, whichever occurs first. Standard rate is 15% thereafter. Optional premium placement is up to 20%. Commission is calculated on gross Order value, excluding applicable taxes and delivery charges.</p>

                <h3>4. Suspension & Termination</h3>
                <p>MEAL BEE may suspend, delist, or terminate a Restaurant Partner's listing upon reasonable belief of fraud, repeated customer complaints, food safety incidents, misrepresentation, or violation of this Agreement or Applicable Laws.</p>

                <p className="agreement-fullref">
                    Read the complete agreement any time from your dashboard under
                    <strong> Restaurant Partner Agreement</strong>.
                </p>
            </>
        )
    },
    delivery_agreement: {
        title: "Delivery Partner Agreement",
        body: (
            <>
                <span className="agreement-updated">Last Updated: July 2026</span>

                <div className="agreement-summary">
                    <h4>In plain terms</h4>
                    <ul>
                        <li><strong>Delivery Partners are independent contractors</strong>, not employees — no PF, gratuity, or employee benefits from MEAL BEE.</li>
                        <li><strong>You're responsible for your own vehicle</strong>, license, insurance, phone, and data — MEAL BEE doesn't cover these costs.</li>
                        <li><strong>Location tracking is used only for order assignment</strong>, navigation, delivery tracking, and safety/fraud prevention — not sold or shared beyond what's needed to complete deliveries.</li>
                        <li><strong>Accounts can be deactivated</strong> for fraud, unsafe conduct, or repeated failed deliveries, with reasons provided where practical.</li>
                    </ul>
                </div>

                <h3>1. Independent Contractor Status</h3>
                <p>Delivery Partners are independent contractors and not employees, agents, or partners of MEAL BEE. Delivery Partners are not entitled to employee benefits, provident fund, gratuity, or social security contributions from MEAL BEE.</p>

                <h3>2. GPS & Tracking</h3>
                <p>Delivery Partners consent to the collection and use of real-time location data via GPS or other technologies solely for Order assignment, route optimization, delivery tracking, safety monitoring, and fraud prevention, handled in accordance with the Privacy Policy.</p>

                <h3>3. Equipment & Costs</h3>
                <p>Delivery Partners are solely responsible for providing and maintaining their own vehicle, valid driving license, fuel, insurance, smartphone, internet connectivity, and all other equipment and expenses required to perform delivery services.</p>

                <h3>4. Deactivation</h3>
                <p>MEAL BEE may deactivate or terminate a Delivery Partner's access for fraud, repeated customer complaints, abusive or unprofessional conduct, violation of traffic or safety laws, or repeated late or failed deliveries.</p>

                <p className="agreement-fullref">
                    Read the complete agreement any time from your dashboard under
                    <strong> Delivery Partner Agreement</strong>.
                </p>
            </>
        )
    }
};

function AgreementModal({ agreementType, onAccept, onDecline }) {
    const [checked, setChecked] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const content = CONTENT[agreementType];

    const handleAccept = async () => {
        if (!checked || submitting) return;
        setSubmitting(true);
        try {
            const token = localStorage.getItem("token");

            // Customer flow runs BEFORE the account exists (still verifying
            // OTP / entering name), so there's no token yet and nothing to
            // record server-side yet - Login.js records it once the
            // account is actually created. Restaurant/delivery accounts
            // already exist and have a token, so we record immediately.
            if (agreementType !== "customer_agreement" && token) {
                await api.post(
                    "/users/accept-agreement",
                    { agreement_type: agreementType },
                    { headers: { Authorization: `Bearer ${token}` } }
                );
            }
            onAccept();
        } catch (error) {
            console.log(error);
            alert("Couldn't record your agreement. Please try again.");
            setSubmitting(false);
        }
    };

    if (!content) return null;

    return (
        <>
            <style>{css}</style>
            <div className="agreement-overlay">
                <div className="agreement-sheet">
                    <div className="agreement-header">
                        <h2 className="agreement-title">{content.title}</h2>
                        <p className="agreement-subtitle">Please review and accept to continue</p>
                    </div>

                    <div className="agreement-scroll">
                        {content.body}
                    </div>

                    <div className="agreement-footer">
                        <label className="agreement-checkbox-row">
                            <input
                                type="checkbox"
                                checked={checked}
                                onChange={(e) => setChecked(e.target.checked)}
                            />
                            <span>I have read and agree to the {content.title}</span>
                        </label>
                        <button
                            className="agreement-accept-btn"
                            onClick={handleAccept}
                            disabled={!checked || submitting}
                        >
                            {submitting ? "Saving…" : "I Agree & Continue"}
                        </button>
                        {onDecline && (
                            <button className="agreement-decline-btn" onClick={onDecline} disabled={submitting}>
                                Cancel
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}

const css = `
    .agreement-overlay {
        position: fixed; inset: 0; background: rgba(0,0,0,0.55);
        display: flex; align-items: center; justify-content: center;
        z-index: 9999; padding: 16px;
    }
    .agreement-sheet {
        background: #ffffff; border-radius: 20px; width: 100%; max-width: 480px;
        max-height: 88vh; display: flex; flex-direction: column; overflow: hidden;
        box-shadow: 0 20px 60px rgba(0,0,0,0.3);
    }
    .agreement-header { padding: 20px 20px 12px; border-bottom: 1px solid #f0f0f0; }
    .agreement-title { font-size: 18px; font-weight: 800; color: #1c1c1c; margin: 0 0 4px; }
    .agreement-subtitle { font-size: 13px; color: #7e7e7e; margin: 0; }

    .agreement-scroll {
        flex: 1; overflow-y: auto; padding: 16px 20px; color: #3d4152; line-height: 1.6;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
    }
    .agreement-scroll h3 { color: #1c1c1c; font-size: 14.5px; font-weight: 800; margin-top: 20px; margin-bottom: 8px; }
    .agreement-scroll p { font-size: 13px; margin-bottom: 12px; }
    .agreement-scroll ul { padding-left: 18px; font-size: 13px; margin-bottom: 12px; }
    .agreement-scroll li { margin-bottom: 6px; }
    .agreement-updated { font-size: 11px; color: #7e7e7e; font-weight: 600; margin-bottom: 16px; display: block; }
    .agreement-summary { background-color: #f4f6f8; border-left: 4px solid #ffbc0d; padding: 14px; border-radius: 0 8px 8px 0; margin-bottom: 16px; }
    .agreement-summary h4 { margin: 0 0 10px 0; font-size: 13px; color: #1c1c1c; font-weight: 800; text-transform: uppercase; letter-spacing: 0.5px; }
    .agreement-summary ul { margin-bottom: 0; }
    .agreement-summary li { color: #1c1c1c; }
    .agreement-fullref { font-size: 12px; color: #7e7e7e; font-style: italic; }

    .agreement-footer { padding: 16px 20px; border-top: 1px solid #f0f0f0; background: #fafafa; }
    .agreement-checkbox-row {
        display: flex; align-items: flex-start; gap: 10px; font-size: 13px;
        color: #1c1c1c; font-weight: 600; margin-bottom: 14px; cursor: pointer;
    }
    .agreement-checkbox-row input { margin-top: 2px; width: 16px; height: 16px; flex-shrink: 0; cursor: pointer; }
    .agreement-accept-btn {
        width: 100%; padding: 15px; background: #ff6b00; color: #fff; border: none;
        border-radius: 12px; font-size: 15px; font-weight: 800; cursor: pointer;
        box-shadow: 0 4px 12px rgba(255,107,0,0.25);
    }
    .agreement-accept-btn:disabled { background: #fcae5a; cursor: not-allowed; box-shadow: none; }
    .agreement-decline-btn {
        width: 100%; padding: 13px; background: transparent; color: #7e7e7e; border: none;
        font-size: 13px; font-weight: 600; cursor: pointer; margin-top: 8px;
    }
`;

export default AgreementModal;